import React, { useCallback, useMemo, useRef } from 'react';
import { Stack } from 'components/stack';
import { IconButton, Popover, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useOpen } from 'AurionCR/components/hooks';
import { FormProvider, useForm } from 'react-hook-form';
import { FormAssignedTo, FormModel } from '../form-assigned-to';
import { isEqual } from 'lodash-es';
import { apiTodoTasks } from 'services/todo-tasks';
import { useEffectError, useMountedRef } from '../../../../hooks';

import { useTranslate } from 'hooks/use-translate';
import { useIsBelowMd } from 'hooks/use-is-below-md';
import style from './index.module.scss';

const useUpdateMutation = apiTodoTasks.useUpdateTodoTaskWithLogMutation;

/** N2 (DC-9): the names a card's face shows before "+N". */
const FACE_NAMES = 2;

interface EditorContainerProps {
  taskID: string;
  userEmployeeProfileIDs: string[];
  onRefresh: () => void;
  onClose: () => void;
}
const EditorContainer: React.FC<React.PropsWithChildren<EditorContainerProps>> = ({
  taskID,
  userEmployeeProfileIDs,
  onClose,
  onRefresh,
}) => {
  const { tp } = useTranslate();

  const [update, resultUpdate] = useUpdateMutation();

  const error = resultUpdate.error;
  useEffectError(error);

  const isLoading = resultUpdate.isLoading;

  const mountedRef = useMountedRef();

  const methods = useForm({
    defaultValues: { userEmployeeProfileIDs },
  });
  const { handleSubmit } = methods;
  const onSubmit = useCallback(
    async (data: FormModel) => {
      const oldList = [...userEmployeeProfileIDs].sort();
      const newList = [...data.userEmployeeProfileIDs].sort();

      if (isEqual(oldList, newList)) {
        return onClose();
      }

      const formData = { id: taskID, userEmployeeProfileIDs: newList };
      await update({
        initData: { userEmployeeProfileIDs, id: taskID },
        formData: formData,
        remark: tp('updates-by-employee'),
      });

      onRefresh();

      if (mountedRef.current) {
        onClose();
      }
    },
    [userEmployeeProfileIDs, onClose, update, onRefresh, mountedRef, taskID, tp],
  );

  return (
    <FormProvider {...methods}>
      <FormAssignedTo isLoading={isLoading} onSave={handleSubmit(onSubmit)} onCancel={onClose} />
    </FormProvider>
  );
};

type Row = {
  id: string;
  assignedTo?: { id: string; fullName: string }[];
};
interface Props {
  row: Row;
  onRefresh: () => void;
}
export const ColumnTaskAssignedTo: React.FC<React.PropsWithChildren<Props>> = ({
  row,
  onRefresh,
}) => {
  const employees = useMemo(() => {
    return row.assignedTo || [];
  }, [row.assignedTo]);

  const ref = useRef(null);
  const disabled = useMemo(() => {
    return employees.length === 0;
  }, [employees]);

  const userEmployeeProfileIDs = useMemo(() => {
    return employees.map(({ id }) => id);
  }, [employees]);

  const { isOpen, handleOpen, handleClose } = useOpen();

  // N2 (T4, DC-9): below md a card's face shows the first two names and "+N";
  // the card's details show them all (the count renders below md only and
  // shows on the face only -- index.module.scss)
  const isBelowMd = useIsBelowMd();
  const hiddenCount = employees.length - FACE_NAMES;

  return (
    <>
      <Stack
        sx={{
          alignItems: 'start',
          justifyContent: 'space-between',
        }}
      >
        <div ref={ref} className={style.names}>
          {employees.map(({ fullName, id }) => {
            return (
              <Typography noWrap title={fullName} key={id} className={style.name}>
                {fullName}
              </Typography>
            );
          })}
          {isBelowMd && hiddenCount > 0 && (
            // `dir`: "+3", not "3+", in the RTL line (a CSS `direction` is
            // flipped by postcss-rtl)
            <Typography className={style.count} dir={'ltr'}>{`+${hiddenCount}`}</Typography>
          )}
        </div>
        <IconButton color={'primary'} disabled={disabled} onClick={handleOpen}>
          <EditIcon />
        </IconButton>
      </Stack>
      <Popover open={isOpen} anchorEl={ref.current} onClose={handleClose}>
        <EditorContainer
          taskID={row.id}
          userEmployeeProfileIDs={userEmployeeProfileIDs}
          onClose={handleClose}
          onRefresh={onRefresh}
        />
      </Popover>
    </>
  );
};
